import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import InnerLayout from "../../components/layouts/inner-layout";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DynamicTable from "../../components/tables/dynamic-table1";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import moment from "moment";
import { productDeleteAction } from "../../redux/actions/product-action";
import {
  deleteItemAction,
  storeTableAction,
} from "../../redux/actions/store-action";
import StoreFilter from "../../components/tables/table-filter/store-filter";
import { DataContext } from "../../components/layouts/main-layout";
import { useHotkeys } from "react-hotkeys-hook";
import {
  storeEmployeeDetailApi,
  storeEmployeeDropdownApi,
} from "../../redux/apis/store-employee-api";
import AccessDenied from "../../components/helper-components/access-denied";

// "Name (CODE)", as the Relationship Manager picker on Store Details shows it.
const rmLabel = (employee) =>
  employee.employee_code
    ? `${employee.employee_name} (${employee.employee_code})`
    : employee.employee_name;

const StoreTable = () => {
  const { permissionFn } = useContext(DataContext);
  // Same access check as Price List: the page needs "read" on
  // document and shows AccessDenied without it.
  const hasAccess = permissionFn("document")?.read === true;
  const dispatch = useDispatch();
  const [storeList, setStoreList] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [tableHeading, setTableHeading] = useState(tabHeading);
  // relationship_manager holds the employee's sem_id; this maps it to the
  // name shown in the RM NAME column, as Store Details labels it.
  const [rmNames, setRmNames] = useState({});
  const [filterData, setFilterData] = useState({
    sort_by: "",
    sort_order: "",
    selectColumn: "",
  });

  useEffect(() => {
    if (!hasAccess) return;
    dispatch(
      storeTableAction(
        {
          pageNumber: currentPage,
          pageSize: pageSize,
          filterData,
        },
        onSuccess,
        onError
      )
    );
  }, [currentPage, pageSize, filterData, reload, hasAccess]);
  // RM NAME is hidden by default; manager names are only fetched once the
  // column is switched on from Show or Hide Columns.
  const rmVisible = !!tableHeading.find(
    (h) => h.value === "relationship_manager"
  )?.isColumnneeded;
  const managersRequested = useRef(false);
  // Employees are read 20 at a time, the page size the other callers of the
  // dropdown use, until totalCount is reached.
  useEffect(() => {
    if (!hasAccess || !rmVisible || managersRequested.current) return;
    managersRequested.current = true;
    const loadManagers = async () => {
      const names = {};
      for (let page = 1; page <= 50; page++) {
        const res = await storeEmployeeDropdownApi({ pageNumber: page, pageSize: 20 });
        if (res?.data?.status === false) {
          console.error("Failed to fetch relationship managers:", res.data?.message);
          break;
        }
        const rows = res.data?.data?.rows ?? [];
        rows.forEach((r) => {
          if (r?.sem_id && r?.employee_name) names[r.sem_id] = rmLabel(r);
        });
        const total = Number(res.data?.data?.totalCount) || 0;
        if (!rows.length || page * 20 >= total) break;
      }
      setRmNames((prev) => ({ ...names, ...prev }));
    };
    loadManagers().catch((err) =>
      console.error("Failed to fetch relationship managers:", err?.response?.data ?? err?.message)
    );
  }, [rmVisible, hasAccess]);
  // Managers the dropdown does not return (e.g. since deleted) are looked up
  // one by one, once each.
  const [lookedUp] = useState(() => new Set());
  useEffect(() => {
    if (!rmVisible) return;
    (storeList ?? [])
      .map((item) => item?.relationship_manager)
      .filter((id) => id && !rmNames[id] && !lookedUp.has(id))
      .forEach((id) => {
        lookedUp.add(id);
        storeEmployeeDetailApi(id)
          .then((res) => {
            const employee = res.data?.data;
            if (res?.data?.status === false || !employee?.employee_name) return;
            setRmNames((prev) => ({ ...prev, [id]: rmLabel(employee) }));
          })
          .catch(() => {});
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeList, rmNames, rmVisible]);
  const onSuccess = (data) => {
    setError(false);
    setLoading(false);
    setStoreList(data?.data?.storeList);
    setDataCount(data?.data?.totalCount);
  };
  const onError = (data) => {
    setError(true);
    setLoading(false);
  };

  const navigate = useNavigate();

  const filteredTableHeading = tableHeading.filter(
    (item) => item.isColumnneeded
  );

  const tableBodyData = storeList?.map((item) => {
    const rowData = {
      id: item?.store_id,
      created_date: item?.created_date
        ? moment(item?.created_date).format("DD/MM/YYYY")
        : "--",
      update_date: item?.update_date
        ? moment(item?.update_date).format("DD/MM/YYYY")
        : "--",
      created_user_name: item?.created_user_name ?? "--",
      updated_user_name: item?.updated_user_name ?? "--",
    };
    filteredTableHeading.forEach((column) => {
      if (column?.value !== "action") {
        if (column.value === "authorised_person_name") {
          rowData.authorised_person_name =
            (
              <span className="fw_500">
                {item?.authorised_person_name ?? "--"}
              </span>
            ) ?? "--";
        } else if (column.value === "relationship_manager") {
          // Older records may hold a name rather than an id; those show as-is.
          rowData.relationship_manager = item?.relationship_manager
            ? rmNames[item.relationship_manager] ?? item.relationship_manager
            : "--";
        } else if (!["created_date", "update_date", "created_user_name", "updated_user_name"].includes(column.value)) {
          rowData[column.value] = item[column.value] ?? "--";
        }
      }
    });
    return rowData;
  });
  const paginationProps = {
    pageSize: pageSize,
    setPageSize: setPageSize,
    dataCount: dataCount,
    setCurrentPage: setCurrentPage,
    currentPage: currentPage,
  };
  const handleFilterClick = useCallback(() => {
    setShowFilter((prevShowFilter) => !prevShowFilter);
  }, []);

  const filterby = showFilter ? (
    <StoreFilter
      setTableHeading={setTableHeading}
      tableHeading={tableHeading}
      filterData={filterData}
      setFilterData={setFilterData}
      setShowFilter={setShowFilter}
    />
  ) : null;

  useHotkeys("escape", () => {
    setShowFilter(false);
  });

  if (!hasAccess) {
    return (
      <InnerLayout title="Store Information" backBtn={true}>
        <AccessDenied message="You don't have permission to view Store Information." />
      </InnerLayout>
    );
  }

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module="document"
      list={true}
      onclkFn={() => navigate("/add-store")}
      title="Store Information"
    >
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <></>
      ) : (
        <DynamicTable
          is_checkbox_req={false}
          onClickItemFn={(id) => navigate(`/view-store/${id}`)}
          list={true}
          onclkFn={() => {
            navigate("/add-store");
          }}
          permissionFn={permissionFn}
          module={"document"}
          date={false}
          path="store"
          id_name="store_id"
          reload={reload}
          setReload={setReload}
          loading={loading}
          setLoading={setLoading}
          deleteAction={deleteItemAction}
          confirmVoid={true}
          label="Store"
          showFilter={showFilter}
          filterFn={handleFilterClick}
          setFilterData={setFilterData}
          filterData={filterData}
          paginationProps={paginationProps}
          tableHeading={filteredTableHeading}
          tableBodyData={tableBodyData}
          placeholder="Search"
          filterBy={filterby}
          filterText="Show or Hide Columns"
          initialFilter={{
            sort_by: "",
            sort_order: "",
            selectColumn: "authorised_person_name",
          }}
        />
      )}
    </InnerLayout>
  );
};

export default StoreTable;
const tabHeading = [
  {
    label: "STORE CODE",
    value: "store_code",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "STORE NAME",
    value: "store_name",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "CONTACT PERSON",
    value: "authorised_person_name",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "MOBILE NUMBER",
    value: "phone_number",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "FIRM NAME",
    value: "firm_name",
    display: true,
    column: true,
    isColumnneeded: true,
  },
  {
    label: "STORE TYPE",
    value: "store_type",
    display: false,
    column: false,
    isColumnneeded: true,
  },
  {
    label: "AREA",
    value: "shop_area",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "CITY",
    value: "shop_city",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "STATE",
    value: "shop_state",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "STATUS",
    value: "store_status",
    display: true,
    column: false,
    isColumnneeded: true,
  },
  {
    label: "REQUEST RECEIVED ON",
    value: "created_date",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "PAYMENT RECEIVE ON",
    value: "payment_recevie_time",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "RM NAME",
    value: "relationship_manager",
    display: true,
    column: false,
    isColumnneeded: false,
  },
  {
    label: "ACTIONS",
    value: "actions",
    display: true,
    column: false,
    isColumnneeded: true,
  },
];
